'use client';

import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import styles from './DayAndNight.module.css';
import gsap from 'gsap';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { SectionHeader } from './SectionHeader';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { drawImageSafe } from 'utils/drawImageSafe';

const Container = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) => (
  <section className={styles.container} {...props}>
    {children}
  </section>
);

const GradientSection = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => (
  <div ref={ref} className={styles.gradientSection} {...props}>
    {children}
  </div>
));
GradientSection.displayName = 'GradientSection';

const StickyOverlayAbs = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={styles.stickyOverlayAbs} {...props}>
    {children}
  </div>
);

const StickyInner = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.stickyInner}>{children}</div>
);

const OverlayInner = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => (
  <div ref={ref} className={styles.overlayInner} {...props}>
    {children}
  </div>
));
OverlayInner.displayName = 'OverlayInner';

const BentoWrapper = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => (
  <div ref={ref} className={styles.bentoWrapper} {...props}>
    {children}
  </div>
));
BentoWrapper.displayName = 'BentoWrapper';

const GradientSequenceContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => (
  <div ref={ref} className={styles.gradientSequenceContainer} {...props}>
    {children}
  </div>
));
GradientSequenceContainer.displayName = 'GradientSequenceContainer';

const SequenceStickyWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.sequenceStickyWrapper}>{children}</div>
);

const SequenceCanvas = React.forwardRef<
  HTMLCanvasElement,
  React.CanvasHTMLAttributes<HTMLCanvasElement>
>((props, ref) => (
  <canvas ref={ref} className={styles.sequenceCanvas} {...props} />
));
SequenceCanvas.displayName = 'SequenceCanvas';

const BentoContent = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.bentoContent}>{children}</div>
);

const BentoImage = (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
  <img alt="" className={styles.bentoImage} {...props} />
);

const ShootingStarsContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => (
  <div ref={ref} className={styles.shootingStarsContainer} {...props}>
    {children}
  </div>
));
ShootingStarsContainer.displayName = 'ShootingStarsContainer';

const Canvas = React.forwardRef<
  HTMLCanvasElement,
  React.CanvasHTMLAttributes<HTMLCanvasElement>
>((props, ref) => <canvas ref={ref} className={styles.canvas} {...props} />);
Canvas.displayName = 'Canvas';

const CustomSubtitleWrapper = ({
  $color,
  children,
}: {
  $color?: string;
  children: React.ReactNode;
}) => (
  <div
    className={styles.customSubtitleWrapper}
    style={
      $color
        ? ({ ['--subtitle-color']: $color } as React.CSSProperties)
        : undefined
    }
  >
    {children}
  </div>
);

const FallbackImage = (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
  <img alt="" className={styles.fallbackImage} {...props} />
);

// Shooting Stars Configuration
const SHOOTING_STARS_CONFIG = {
  // Speed configuration (pixels per frame at 60fps)
  SPEED_MIN: 8,
  SPEED_MAX: 14,

  // Star trail length (pixels)
  LENGTH_MIN: 100,
  LENGTH_MAX: 200,

  // Generation frequency (milliseconds between new stars)
  GENERATION_MIN_MS: 2000,
  GENERATION_MAX_MS: 3000,

  // Angle constraints (degrees, measured from vertical)
  // 0° = straight down, positive = clockwise (right), negative = counter-clockwise (left)
  MIN_ANGLE_DEG: -45,
  MAX_ANGLE_DEG: 45,

  // Path length range (pixels)
  PATH_LENGTH_MIN: 964,
  PATH_LENGTH_MAX: 2000,

  // Lifecycle percentages (0-1)
  GROW_START: 0,
  GROW_END: 0.2,
  FULL_START: 0.2,
  FULL_END: 0.8,
  SHRINK_START: 0.8,
  SHRINK_END: 1,

  // Visual properties
  LINE_WIDTH: 3,
  SHADOW_BLUR: 9,
  SHADOW_COLOR: 'rgba(255, 255, 255, 0.8)',

  // Gradient color stops
  GRADIENT_START_COLOR: 'rgba(255, 255, 255, 0.8)',
  GRADIENT_MID_COLOR: 'rgba(255, 255, 255, 0.5)',
  GRADIENT_END_COLOR: 'rgba(255, 255, 255, 0)',
  GRADIENT_MID_STOP: 0.6,
} as const;

interface Star {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  currentX: number;
  currentY: number;
  speed: number; // pixels per frame
  distance: number; // total path length
  lifetime: number; // current frame
  maxLifetime: number; // total frames to travel the path
  length: number; // visual length of the star trail
  maxLength: number; // maximum length (100-120px, set once)
  opacity: number;
  angle: number; // angle of the path
}

const SEQUENCE_DIR = '/web_v2/uh-home-v2/sequences/day-night-v8';

const RESOLUTION_MAP = {
  mobile: {
    tag: 'w_800',
    dir: 'mobile',
    startIndex: 0,
    endIndex: 175,
    frameStep: 2,
  },
  desktop: {
    tag: 'w_1920',
    dir: 'desktop',
    startIndex: 0,
    endIndex: 175,
    frameStep: 2,
  },
} as const;

const getFrameCount = (isMobile: boolean) => {
  const res = isMobile ? RESOLUTION_MAP.mobile : RESOLUTION_MAP.desktop;
  return Math.floor((res.endIndex - res.startIndex) / res.frameStep + 1);
};

const frameUrl = (idx: number, isMobile: boolean) => {
  const res = isMobile ? RESOLUTION_MAP.mobile : RESOLUTION_MAP.desktop;
  return getCompressedAssetUrl(
    `${SEQUENCE_DIR}/${res.dir}/${String(idx).padStart(5, '0')}.png/${res.tag}`,
  );
};

const GradientSequence = React.memo(({ isMobile }: { isMobile: boolean }) => {
  const t = useTranslations('uhHome');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const renderState = useRef({ frame: 0 });
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const lastDrawnFrameRef = useRef<number>(-1);
  const [isReady, setIsReady] = useState(false);
  const gsapCtx = useGsapContext();
  const { add } = useMasterTimeline();
  const loadedDeviceTypeRef = useRef<'mobile' | 'desktop' | null>(null);

  useEffect(() => {
    const currentDeviceType = isMobile ? 'mobile' : 'desktop';

    // Skip if we've already loaded images for this device type
    if (loadedDeviceTypeRef.current === currentDeviceType) return;

    let isCancelled = false;
    const frameCount = getFrameCount(isMobile);
    const loaded: HTMLImageElement[] = new Array(frameCount);

    const loadOne = (frameIndex: number, arrayIndex: number) =>
      new Promise<void>((resolve) => {
        const img = new Image();
        img.loading = 'eager';
        (img as any).fetchPriority = 'high';
        img.src = frameUrl(frameIndex, isMobile);
        img.onload = img.onerror = () => {
          loaded[arrayIndex] = img;
          resolve();
        };
      });

    const CONCURRENCY = 8;
    let arrayIdx = 0;
    let inflight = 0;

    const res = isMobile ? RESOLUTION_MAP.mobile : RESOLUTION_MAP.desktop;

    const pump = () => {
      if (isCancelled) return;
      if (arrayIdx >= frameCount && inflight === 0) {
        imagesRef.current = loaded;
        loadedDeviceTypeRef.current = currentDeviceType;
        setIsReady(true);
        return;
      }
      while (inflight < CONCURRENCY && arrayIdx < frameCount) {
        const currentArrayIdx = arrayIdx;
        const currentFrameIdx = res.startIndex + arrayIdx * res.frameStep;
        arrayIdx++;
        inflight++;
        loadOne(currentFrameIdx, currentArrayIdx).then(() => {
          inflight--;
          pump();
        });
      }
    };

    pump();

    return () => {
      isCancelled = true;
    };
  }, [isMobile]);

  const canvasDimensionsRef = useRef({ width: 0, height: 0 });
  const drawParamsRef = useRef({
    drawX: 0,
    drawY: 0,
    drawWidth: 0,
    drawHeight: 0,
  });

  const drawFrame = (frameIdx: number) => {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;

    const roundedIdx = Math.round(frameIdx);
    // Skip if same frame as last drawn
    if (lastDrawnFrameRef.current === roundedIdx) return;

    const img = imagesRef.current[roundedIdx];
    if (!img || !img.complete || img.naturalWidth === 0) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const { drawX, drawY, drawWidth, drawHeight } = drawParamsRef.current;
    if (!drawImageSafe(ctx, img, drawX, drawY, drawWidth, drawHeight)) return;
    lastDrawnFrameRef.current = roundedIdx;
  };

  const calculateDrawParams = () => {
    const displayWidth = canvasDimensionsRef.current.width;
    const displayHeight = canvasDimensionsRef.current.height;

    if (imagesRef.current.length === 0) return;
    const img = imagesRef.current[0];
    if (!img || img.naturalWidth === 0) return;

    const imgAspect = img.naturalWidth / img.naturalHeight;
    const canvasAspect = displayWidth / displayHeight;

    let drawWidth: number;
    let drawHeight: number;
    let drawX: number;
    let drawY: number;

    if (imgAspect > canvasAspect) {
      drawHeight = displayHeight;
      drawWidth = drawHeight * imgAspect;
      drawX = (displayWidth - drawWidth) / 2;
      drawY = 0;
    } else {
      drawWidth = displayWidth;
      drawHeight = drawWidth / imgAspect;
      drawX = 0;
      drawY = (displayHeight - drawHeight) / 2;
    }

    drawParamsRef.current = { drawX, drawY, drawWidth, drawHeight };
  };

  useIsomorphicLayoutEffect(() => {
    if (!isReady || !canvasRef.current) return;
    const canvas = canvasRef.current;

    // Get and cache context once
    const ctx = canvas.getContext('2d', { desynchronized: true });
    if (!ctx) return;
    ctxRef.current = ctx;

    const updateCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      canvasDimensionsRef.current = { width: rect.width, height: rect.height };
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      calculateDrawParams();
      drawFrame(renderState.current.frame);
    };
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    drawFrame(0);
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [isReady]);

  useIsomorphicLayoutEffect(() => {
    if (
      !isReady ||
      !sectionRef.current ||
      !canvasRef.current ||
      imagesRef.current.length === 0
    ) {
      return;
    }

    gsapCtx.add(() => {
      const triggerEl = sectionRef.current;
      if (!triggerEl) return;
      const totalFrames = imagesRef.current.length - 1;
      const scrubObj = renderState.current;

      const tl = gsap.timeline({
        defaults: { duration: 1 },
        scrollTrigger: {
          immediateRender: false,
          trigger: triggerEl,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.5,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      const render = () => {
        requestAnimationFrame(() => {
          drawFrame(scrubObj.frame);
        });
      };

      tl.fromTo(
        scrubObj,
        { frame: 0 },
        {
          frame: totalFrames,
          snap: 'frame',
          ease: 'none',
          onUpdate: render,
          duration: 1,
        },
      );

      // Register with the MasterTimeline for orchestration/debug overlay
      try {
        add(tl, 'day-night-gradient-sequence', 0);
      } catch (e) {
        // no-op
      }

      return () => {
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });
  }, [isReady, gsapCtx, add]);

  return (
    <GradientSequenceContainer ref={sectionRef}>
      <SequenceStickyWrapper>
        <SequenceCanvas
          ref={canvasRef}
          role="img"
          aria-label={t('dayAndNight.sequenceCanvas.ariaLabel.dayNightGradient')}
        />
      </SequenceStickyWrapper>
    </GradientSequenceContainer>
  );
});

GradientSequence.displayName = 'GradientSequence';

const ShootingStars = React.memo(
  ({
    containerRef,
  }: {
    containerRef?: React.RefObject<HTMLDivElement | null>;
  }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationFrameRef = useRef<number | undefined>(undefined);
    const starsRef = useRef<Star[]>([]);
    const lastStarTimeRef = useRef<number>(0);
    const canvasDimensionsRef = useRef({ width: 0, height: 0 });
    const isPausedRef = useRef<boolean>(false);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d', {
        alpha: true,
        desynchronized: true,
      });
      if (!ctx) return;

      // Clear existing stars when config changes
      starsRef.current = [];
      lastStarTimeRef.current = 0;

      // Set canvas size
      const updateCanvasSize = () => {
        const rect = canvas.getBoundingClientRect();
        canvasDimensionsRef.current = {
          width: rect.width,
          height: rect.height,
        };
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
      };

      updateCanvasSize();
      window.addEventListener('resize', updateCanvasSize);

      // Create a new star with point A and angle-constrained point B
      const createStar = (): Star => {
        const { width: rectWidth, height: rectHeight } =
          canvasDimensionsRef.current;

        // Generate point A randomly (start point - smaller y for downward travel)
        const pointAX = Math.random() * rectWidth;
        // Start in upper portion to allow downward travel
        const pointAY = Math.random() * (rectHeight * 0.7);

        // Generate random angle within constraints (degrees)
        const angleDeg =
          Math.random() *
            (SHOOTING_STARS_CONFIG.MAX_ANGLE_DEG -
              SHOOTING_STARS_CONFIG.MIN_ANGLE_DEG) +
          SHOOTING_STARS_CONFIG.MIN_ANGLE_DEG;

        // Convert angle to radians
        const angleRad = (angleDeg * Math.PI) / 180;

        // Generate random path length
        const pathLength =
          Math.random() *
            (SHOOTING_STARS_CONFIG.PATH_LENGTH_MAX -
              SHOOTING_STARS_CONFIG.PATH_LENGTH_MIN) +
          SHOOTING_STARS_CONFIG.PATH_LENGTH_MIN;

        // Calculate point B from point A using angle and path length
        // For downward travel, we need positive y component
        // Angle is measured from horizontal: -60 to 60 degrees
        // To ensure downward travel, we'll use the angle to determine direction
        // and always make y component positive
        const xComponent = Math.cos(angleRad) * pathLength;
        const yComponent = Math.abs(Math.sin(angleRad)) * pathLength; // Always downward
        let pointBX = pointAX + xComponent;
        let pointBY = pointAY + yComponent;

        // Clamp point B to canvas bounds
        pointBX = Math.max(0, Math.min(rectWidth, pointBX));
        pointBY = Math.max(0, Math.min(rectHeight, pointBY));

        // Start point (smaller y) and end point (greater y) for downward travel
        const startX = pointAX;
        const startY = pointAY;
        const endX = pointBX;
        const endY = pointBY;

        // Calculate distance and angle for the actual path
        const dx = endX - startX;
        const dy = endY - startY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);

        // Generate a fixed random speed (pixels per frame)
        const speed =
          Math.random() *
            (SHOOTING_STARS_CONFIG.SPEED_MAX -
              SHOOTING_STARS_CONFIG.SPEED_MIN) +
          SHOOTING_STARS_CONFIG.SPEED_MIN;

        // Calculate lifetime based on distance and speed
        const maxLifetimeFrames = Math.ceil(distance / speed);

        // Set a fixed random length
        const maxLength =
          Math.random() *
            (SHOOTING_STARS_CONFIG.LENGTH_MAX -
              SHOOTING_STARS_CONFIG.LENGTH_MIN) +
          SHOOTING_STARS_CONFIG.LENGTH_MIN;

        return {
          startX,
          startY,
          endX,
          endY,
          currentX: startX,
          currentY: startY,
          speed,
          distance,
          lifetime: 0,
          maxLifetime: maxLifetimeFrames,
          length: 0,
          maxLength,
          opacity: 0,
          angle,
        };
      };

      // Update and draw stars
      const animate = (currentTime: number) => {
        // Skip rendering if paused (not visible)
        if (!isPausedRef.current) {
          // Generate new stars based on configured frequency
          const generationDelay =
            Math.random() *
              (SHOOTING_STARS_CONFIG.GENERATION_MAX_MS -
                SHOOTING_STARS_CONFIG.GENERATION_MIN_MS) +
            SHOOTING_STARS_CONFIG.GENERATION_MIN_MS;
          if (currentTime - lastStarTimeRef.current > generationDelay) {
            starsRef.current.push(createStar());
            lastStarTimeRef.current = currentTime;
          }

          // Clear canvas
          const { width, height } = canvasDimensionsRef.current;
          ctx.clearRect(0, 0, width, height);

          // Update and draw stars
          starsRef.current = starsRef.current.filter((star) => {
            star.lifetime++;

            // Calculate progress along the path (0 to 1)
            const progress = star.lifetime / star.maxLifetime;

            // Update position along the line based on progress
            star.currentX = star.startX + (star.endX - star.startX) * progress;
            star.currentY = star.startY + (star.endY - star.startY) * progress;

            // Update length and opacity based on lifecycle percentages
            const { GROW_START, GROW_END, FULL_END, SHRINK_START, SHRINK_END } =
              SHOOTING_STARS_CONFIG;

            if (progress <= GROW_START) {
              // Start: opacity 0, length 0px
              star.length = 0;
              star.opacity = 0;
            } else if (progress < GROW_END) {
              // Grow phase: grow from 0 to full
              const phaseProgress =
                (progress - GROW_START) / (GROW_END - GROW_START);
              star.length = phaseProgress * star.maxLength;
              star.opacity = phaseProgress;
            } else if (progress < FULL_END) {
              // Full phase: maintain full length and opacity
              star.length = star.maxLength;
              star.opacity = 1;
            } else if (progress < SHRINK_END) {
              // Shrink phase: shrink back to 0
              const phaseProgress =
                (progress - SHRINK_START) / (SHRINK_END - SHRINK_START);
              star.length = (1 - phaseProgress) * star.maxLength;
              star.opacity = 1 - phaseProgress;
            } else {
              // End: opacity 0, length 0px
              star.length = 0;
              star.opacity = 0;
            }

            // Draw star - trail backwards along the path direction
            ctx.save();
            ctx.globalAlpha = star.opacity;

            // Calculate the unit vector along the path (from start to end)
            const pathDx = star.endX - star.startX;
            const pathDy = star.endY - star.startY;
            const pathLength = Math.sqrt(pathDx * pathDx + pathDy * pathDy);
            const unitX = pathLength > 0 ? pathDx / pathLength : 0;
            const unitY = pathLength > 0 ? pathDy / pathLength : 0;

            // Trail points backwards from current position (opposite of travel direction)
            const trailStartX = star.currentX;
            const trailStartY = star.currentY;
            const trailEndX = star.currentX - unitX * star.length;
            const trailEndY = star.currentY - unitY * star.length;

            // Validate coordinates are finite before creating gradient
            if (
              !Number.isFinite(trailStartX) ||
              !Number.isFinite(trailStartY) ||
              !Number.isFinite(trailEndX) ||
              !Number.isFinite(trailEndY)
            ) {
              return star.lifetime < star.maxLifetime;
            }

            // Create gradient for star trail along the path
            // Bright at current position, fading backwards
            const gradient = ctx.createLinearGradient(
              trailStartX,
              trailStartY,
              trailEndX,
              trailEndY,
            );
            gradient.addColorStop(
              0,
              SHOOTING_STARS_CONFIG.GRADIENT_START_COLOR,
            );
            gradient.addColorStop(
              SHOOTING_STARS_CONFIG.GRADIENT_MID_STOP,
              SHOOTING_STARS_CONFIG.GRADIENT_MID_COLOR,
            );
            gradient.addColorStop(1, SHOOTING_STARS_CONFIG.GRADIENT_END_COLOR);

            ctx.strokeStyle = gradient;
            ctx.lineWidth = SHOOTING_STARS_CONFIG.LINE_WIDTH;
            ctx.shadowBlur = SHOOTING_STARS_CONFIG.SHADOW_BLUR;
            ctx.shadowColor = SHOOTING_STARS_CONFIG.SHADOW_COLOR;
            ctx.beginPath();
            ctx.moveTo(trailStartX, trailStartY);
            ctx.lineTo(trailEndX, trailEndY);
            ctx.stroke();
            ctx.restore();

            // Remove star if lifetime exceeded
            return star.lifetime < star.maxLifetime;
          });
        }

        animationFrameRef.current = requestAnimationFrame(animate);
      };

      // Set up Intersection Observer to pause/resume animation
      let observer: IntersectionObserver | null = null;
      if (canvas) {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              isPausedRef.current = !entry.isIntersecting;
            });
          },
          {
            rootMargin: '100px',
            threshold: 0,
          },
        );
        observer.observe(canvas);
      }

      animationFrameRef.current = requestAnimationFrame(animate);

      return () => {
        window.removeEventListener('resize', updateCanvasSize);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        observer?.disconnect();
      };
    }, []);

    return (
      <ShootingStarsContainer ref={containerRef} aria-hidden="true">
        <Canvas ref={canvasRef} />
      </ShootingStarsContainer>
    );
  },
);

ShootingStars.displayName = 'ShootingStars';
const DayAndNight = React.memo(() => {
  const t = useTranslations('uhHome');
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);
  const textRef2 = useRef<HTMLDivElement | null>(null);
  const shootingStarsRef = useRef<HTMLDivElement | null>(null);
  const bentoContentRef = useRef<HTMLDivElement | null>(null);
  const gsapCtx = useGsapContext();
  const { add } = useMasterTimeline();
  const { width } = useWindowSize();
  const isMobile = width <= 767;

  const reportBentoUrl = useMemo(
    () =>
      getCompressedAssetUrl(
        isMobile
          ? '/web_v2/uh-home-v2/sleep-report/very-final/mobile.png'
          : '/web_v2/uh-home-v2/sleep-report/very-final/desktop-final.png',
      ),
    [isMobile],
  );

  const lastFrameUrl = useMemo(
    () =>
      frameUrl(
        isMobile
          ? RESOLUTION_MAP.mobile.endIndex
          : RESOLUTION_MAP.desktop.endIndex,
        isMobile,
      ),
    [isMobile],
  );

  useIsomorphicLayoutEffect(() => {
    if (
      !sectionRef.current ||
      !textRef.current ||
      !textRef2.current ||
      !shootingStarsRef.current ||
      !bentoContentRef.current
    )
      return;

    gsapCtx.add(() => {
      const triggerEl = sectionRef.current;
      const textEl = textRef.current;
      const textEl2 = textRef2.current;
      const starsEl = shootingStarsRef.current;
      const bentoEl = bentoContentRef.current;

      if (!triggerEl || !textEl || !textEl2 || !starsEl || !bentoEl) return;

      const tl = gsap.timeline({
        defaults: { ease: 'power2.inOut' },
        scrollTrigger: {
          trigger: triggerEl,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.5,
          invalidateOnRefresh: true,
        },
      });

      try {
        add(tl, 'home-day-and-night-sticky-text', 1);
      } catch (e) {
        // no-op
      }

      gsap.set(textEl, {
        opacity: 1,
        filter: 'blur(0px)',
      });
      gsap.set(textEl2, {
        opacity: 0,
        filter: 'blur(8px)',
      });
      gsap.set(starsEl, {
        opacity: 0,
      });
      gsap.set(bentoEl, {
        opacity: 0,
      });

      tl.fromTo(
        textEl,
        {
          opacity: 1,
          filter: 'blur(0px)',
        },
        {
          opacity: 0,
          filter: 'blur(8px)',
          duration: 0.05,
          ease: 'power2.inOut',
        },
        0.25,
      );

      tl.fromTo(
        textEl2,
        {
          opacity: 0,
          filter: 'blur(8px)',
        },
        {
          opacity: 1,
          filter: 'blur(0px)',
          duration: 0.05,
          ease: 'power2.inOut',
        },
        '+=0',
      );

      tl.fromTo(
        textEl2,
        {
          opacity: 1,
          filter: 'blur(0px)',
        },
        {
          opacity: 0,
          filter: 'blur(8px)',
          duration: 0.08,
          ease: 'power2.inOut',
        },
        '+=0.5',
      );

      tl.to(
        starsEl,
        {
          opacity: 1,
          duration: 0.015,
          ease: 'power2.inOut',
        },
        '-=0.05',
      );

      tl.to(
        starsEl,
        {
          opacity: 0,
          duration: 0.6,
          ease: 'power2.inOut',
        },
        0.58,
      );

      tl.fromTo(
        bentoEl,
        {
          opacity: 0,
          filter: 'blur(8px)',
          y: 100,
        },
        {
          opacity: 1,
          filter: 'blur(0px)',
          y: 0,
          duration: 0.4,
          ease: 'power2.out',
        },
        '+=0',
      );

      return () => {
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });
  }, [gsapCtx, add]);

  return (
    <Container
      role="region"
      aria-label={t('dayAndNight.container.ariaLabel.dayNightSection')}
    >
      <GradientSection ref={sectionRef}>
        <StickyOverlayAbs aria-hidden="false">
          <StickyInner>
            <OverlayInner ref={textRef}>
              <CustomSubtitleWrapper $color="#000000">
                <SectionHeader
                  subtitle={t(
                    'dayAndNight.sectionHeader.subtitle.homeIdentifiesSubtle',
                  )}
                  variant="light"
                  marginBottom="0"
                />
              </CustomSubtitleWrapper>
            </OverlayInner>
            <OverlayInner ref={textRef2}>
              <CustomSubtitleWrapper $color="#ffffff">
                <SectionHeader
                  subtitle={t(
                    'dayAndNight.sectionHeader.subtitle.throughoutNightHome',
                  )}
                  variant="dark"
                  marginBottom="0"
                />
              </CustomSubtitleWrapper>
            </OverlayInner>
            <BentoWrapper ref={bentoContentRef}>
              <BentoContent>
                <CustomSubtitleWrapper $color="#ffffff">
                  <SectionHeader
                    subtitle={t('dayAndNight.sectionHeader.subtitle.youWakeUp')}
                    variant="dark"
                    marginBottom="0"
                  />
                </CustomSubtitleWrapper>
                <BentoImage
                  src={reportBentoUrl}
                  alt={t('dayAndNight.bentoImage.alt.sleepReportShowing')}
                />
              </BentoContent>
            </BentoWrapper>
          </StickyInner>
          <ShootingStars containerRef={shootingStarsRef} />
        </StickyOverlayAbs>
        <GradientSequence isMobile={isMobile} />
        <FallbackImage
          src={lastFrameUrl}
          alt={t('dayAndNight.fallbackImage.alt.dayNightGradient')}
          aria-hidden="true"
        />
      </GradientSection>
    </Container>
  );
});

DayAndNight.displayName = 'DayAndNight';

export default DayAndNight;
